import { useEffect, useRef, useState, type FormEvent } from "react";
import type {
  AdminSubscriber,
  SubscriberBroadcastInput,
} from "@tih/shared";
import {
  ApiError,
  adminBroadcastToSubscribers,
  adminCreateSubscriber,
  adminDeleteSubscriber,
  adminFetchSubscribers,
  adminUpdateSubscriber,
} from "../../api/client";

const EMPTY_SUBSCRIBER = { name: "", email: "" };

export function AdminSubscribersPage() {
  const formRef = useRef<HTMLElement>(null);
  const [rows, setRows] = useState<AdminSubscriber[]>([]);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [subscriberForm, setSubscriberForm] = useState({ ...EMPTY_SUBSCRIBER });
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcast, setBroadcast] = useState<SubscriberBroadcastInput>({
    subject: "",
    body: "",
  });

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetchSubscribers();
      setRows(res.subscribers);
      setSmtpConfigured(res.smtpConfigured);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function copyAll() {
    const text = rows.map((r) => r.email).join("\n");
    void navigator.clipboard.writeText(text);
    setSuccess("Copied all emails to clipboard.");
  }

  function startAdd() {
    setEditingId(null);
    setSubscriberForm({ ...EMPTY_SUBSCRIBER });
    setShowAdd(true);
    setError(null);
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function startEdit(row: AdminSubscriber) {
    setShowAdd(false);
    setEditingId(row.id);
    setSubscriberForm({
      name: row.name ?? row.displayName ?? "",
      email: row.email,
    });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function cancelSubscriberForm() {
    setShowAdd(false);
    setEditingId(null);
    setSubscriberForm({ ...EMPTY_SUBSCRIBER });
  }

  async function saveSubscriber(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    const email = subscriberForm.email.trim();
    const name = subscriberForm.name.trim();
    if (!email) {
      setError("Email is required");
      setBusy(false);
      return;
    }
    try {
      if (editingId != null) {
        await adminUpdateSubscriber(editingId, {
          email,
          name: name || null,
        });
        setSuccess("Subscriber updated.");
      } else {
        await adminCreateSubscriber({
          email,
          ...(name ? { name } : {}),
        });
        setSuccess("Subscriber added.");
      }
      cancelSubscriberForm();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save subscriber");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    if (!confirm("Remove this subscriber?")) return;
    setBusy(true);
    try {
      await adminDeleteSubscriber(id);
      if (editingId === id) cancelSubscriberForm();
      setSuccess("Subscriber removed.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete");
    } finally {
      setBusy(false);
    }
  }

  async function sendBroadcast(testOnly: boolean) {
    if (!broadcast.subject.trim() || !broadcast.body.trim()) {
      setError("Subject and message are required.");
      return;
    }
    if (!testOnly && !confirm(`Send this email to all ${rows.length} subscribers?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await adminBroadcastToSubscribers({
        subject: broadcast.subject.trim(),
        body: broadcast.body.trim(),
        testOnly,
      });
      setSuccess(
        result.testOnly
          ? `Test email sent to your admin address (${result.sent} recipient).`
          : `Broadcast sent to ${result.sent} subscriber${result.sent === 1 ? "" : "s"} in ${result.batches} batch${result.batches === 1 ? "" : "es"}.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send broadcast");
    } finally {
      setBusy(false);
    }
  }

  const showSubscriberForm = showAdd || editingId != null;

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>Subscribers</h1>
          <p>
            {rows.length} email subscriber{rows.length === 1 ? "" : "s"} on the list.
            Names come from the signup form, RSVP, or account when available.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={startAdd}>
          + Add subscriber
        </button>
      </header>

      {!smtpConfigured && (
        <div className="admin-event-context-banner" style={{ marginBottom: 16 }}>
          Outbound email is not configured — configure SMTP in{" "}
          <a href="/admin/settings/email">Settings → Email</a> to send broadcasts. You can still
          manage the list and copy emails.
        </div>
      )}

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      <section className="admin-card" style={{ marginBottom: 24 }}>
        <button
          type="button"
          className="admin-live-event-summary"
          onClick={() => setBroadcastOpen((v) => !v)}
          aria-expanded={broadcastOpen}
          style={{ marginBottom: broadcastOpen ? 16 : 0 }}
        >
          <div className="admin-live-event-summary-main">
            <strong>Broadcast email</strong>
            <span className="admin-live-event-when">
              Compose a message to all {rows.length} subscribers
            </span>
          </div>
          <span className="admin-badge admin-badge-upcoming">
            {broadcastOpen ? "Close" : "Compose"}
          </span>
        </button>

        {broadcastOpen && (
          <div className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Subject</span>
                <input
                  value={broadcast.subject}
                  disabled={busy}
                  onChange={(e) =>
                    setBroadcast((b) => ({ ...b, subject: e.target.value }))
                  }
                  placeholder="Together In Heaven — July worship overnight"
                />
              </label>
              <label>
                <span>Message</span>
                <textarea
                  rows={10}
                  value={broadcast.body}
                  disabled={busy}
                  onChange={(e) => setBroadcast((b) => ({ ...b, body: e.target.value }))}
                  placeholder={"Dear friends,\n\nWe're gathering again on…"}
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy || !smtpConfigured}
                onClick={() => void sendBroadcast(true)}
              >
                Send test to me
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !smtpConfigured || rows.length === 0}
                onClick={() => void sendBroadcast(false)}
              >
                {busy ? "Sending…" : `Send to all (${rows.length})`}
              </button>
            </div>
          </div>
        )}
      </section>

      <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn btn-ghost" onClick={copyAll} disabled={rows.length === 0}>
          Copy all emails
        </button>
      </div>

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ opacity: 0.6 }}>No subscribers yet.</div>
      ) : (
        <div className="admin-table-wrap" style={{ marginBottom: 24 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Joined</th>
                <th>Name</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>{r.displayName ?? r.name ?? "—"}</td>
                  <td>
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                  </td>
                  <td className="admin-row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => startEdit(r)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={busy}
                      onClick={() => void remove(r.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSubscriberForm && (
        <section ref={formRef} className="admin-card">
          <h2 className="admin-card-title">
            {editingId != null ? "Edit subscriber" : "Add subscriber"}
          </h2>
          <form onSubmit={(e) => void saveSubscriber(e)} className="admin-blog-form">
            <div className="admin-blog-form-fields">
              <label>
                <span>Name (optional)</span>
                <input
                  value={subscriberForm.name}
                  disabled={busy}
                  onChange={(e) =>
                    setSubscriberForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Grace M."
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={subscriberForm.email}
                  disabled={busy}
                  onChange={(e) =>
                    setSubscriberForm((f) => ({ ...f, email: e.target.value }))
                  }
                  required
                />
              </label>
            </div>
            <div className="admin-blog-form-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : editingId != null ? "Save changes" : "Add subscriber"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={cancelSubscriberForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
